import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, Checkbox, Divider, Stack, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import JobCandidateCard from './JobCandidateCard';
import InvitationDialog from './InvitationDialog';

const JobCandidates = (props) => {
  const {candidates, candidatesError, candidateRadius, jobBackend, jobData, setCandidateRadius} = props;

  const [radiusInput, setRadiusInput] = React.useState(String(candidateRadius));
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [isInvitationOpen, setIsInvitationOpen] = React.useState(false);

  const radiusKm = Number(radiusInput);
  const isRadiusValid = Number.isInteger(radiusKm) && radiusKm >= 1 && radiusKm <= 50;
  const located = candidates?.candidates || [];
  const unlocated = candidates?.unlocated || [];
  const selectedCandidates = [...located, ...unlocated].filter((candidate) => {
    return candidate.selectable && selectedIds.includes(candidate.caregiverId);
  });

  React.useEffect(() => {
    if (!isRadiusValid || radiusKm === candidateRadius) {
      return;
    }

    const timeout = setTimeout(() => setCandidateRadius(radiusKm), 400);
    return () => clearTimeout(timeout);
  }, [candidateRadius, isRadiusValid, radiusKm, setCandidateRadius]);

  const renderCandidates = (rows) => {
    const rowIds = rows.filter((candidate) => candidate.selectable).map((candidate) => candidate.caregiverId);
    const selectedCount = rowIds.filter((caregiverId) => selectedIds.includes(caregiverId)).length;
    const areAllSelected = rowIds.length > 0 && selectedCount === rowIds.length;

    return (
      <Box sx={{overflowX: 'auto', pb: 1}}>
        <Box sx={{minWidth: 1500}}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '48px 56px minmax(160px, 1.2fr) 90px minmax(170px, 1fr) minmax(200px, 1.3fr) 110px 160px 190px 170px',
              gap: 1.5,
              alignItems: 'center',
              backgroundColor: '#F9F8F6',
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1,
            }}
          >
            <Checkbox
              label=""
              ariaLabel={translate(areAllSelected ? 'career.jobDetail.Candidates.DeselectAll' : 'career.jobDetail.Candidates.SelectAll')}
              checked={areAllSelected}
              disabled={rowIds.length === 0}
              indeterminate={selectedCount > 0 && !areAllSelected}
              onChange={(isSelected) => setSelectedIds((selected) => [
                ...selected.filter((caregiverId) => !rowIds.includes(caregiverId)),
                ...(isSelected ? rowIds : []),
              ])}
              formControlSx={{m: 0}}
              checkBoxSx={{p: 0.5}}
            />
            <Box />
            {
              Object.keys(translate('career.jobDetail.Candidates.Columns')).map((column) => (
                <Typography key={column} variant="caption" fontWeight={700}>
                  <Translate expression={`career.jobDetail.Candidates.Columns.${column}`} />
                </Typography>
              ))
            }
          </Box>
          {
            rows.map((candidate) => (
              <JobCandidateCard
                key={candidate.caregiverId}
                candidate={candidate}
                isSelected={selectedIds.includes(candidate.caregiverId)}
                onSelectionChange={(isSelected) => setSelectedIds((selected) => {
                  return isSelected ?
                    [...selected, candidate.caregiverId] :
                    selected.filter((caregiverId) => caregiverId !== candidate.caregiverId);
                })}
              />
            ))
          }
        </Box>
      </Box>
    );
  };

  return (
    <Stack spacing={2} pb={2}>
      <Divider />
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={1}
        alignItems={{xs: 'stretch', md: 'flex-start'}}
        justifyContent="space-between"
        px={2}
      >
        <TextField
          size="small"
          type="number"
          label={translate('career.jobDetail.Candidates.Radius')}
          value={radiusInput}
          onChange={setRadiusInput}
          helperText={isRadiusValid ? null : translate('career.jobDetail.Candidates.Errors.400')}
          slotProps={{
            htmlInput: {min: 1, max: 50, step: 1},
            formHelperText: {sx: {color: 'error.main'}},
          }}
          sx={{width: {xs: '100%', md: 220}}}
        />
        <Button
          disabled={selectedCandidates.length === 0 || jobData?.status !== 'ACTIVE'}
          onClick={() => setIsInvitationOpen(true)}
          sx={{whiteSpace: 'nowrap'}}
        >
          <Translate expression="career.jobDetail.Candidates.Send" substitute={{count: selectedCandidates.length}} />
        </Button>
      </Stack>
      {
        jobData?.status !== 'ACTIVE' &&
        <Alert severity="info" sx={{mx: 2}}>
          <Translate expression="career.jobDetail.Candidates.NotActive" />
        </Alert>
      }
      {
        candidatesError &&
        <Alert severity="error" sx={{mx: 2}}>
          <Translate
            expression={`career.jobDetail.Candidates.Errors.${
              [400, 404, 409].includes(candidatesError.status) ? candidatesError.status : 'Default'
            }`}
          />
        </Alert>
      }
      {
        candidates && located.length === 0 &&
        <Typography color="text.secondary" align="center">
          <Translate expression="career.jobDetail.Candidates.Empty" substitute={{radius: candidates.radiusKm}} />
        </Typography>
      }
      {
        located.length > 0 &&
        renderCandidates(located)
      }
      {
        unlocated.length > 0 &&
        <Stack spacing={1}>
          <Typography fontWeight={600} px={2}>
            <Translate expression="career.jobDetail.Candidates.UnlocatedTitle" substitute={{count: unlocated.length}} />
          </Typography>
          {renderCandidates(unlocated)}
        </Stack>
      }
      {
        isInvitationOpen &&
        <InvitationDialog
          caregivers={selectedCandidates}
          onClose={(isAttempted) => {
            setIsInvitationOpen(false);

            if (isAttempted) {
              setSelectedIds([]);
              jobBackend.invitations.reload();
            }
          }}
          onSend={jobBackend.invitations.send}
        />
      }
    </Stack>
  );
};

JobCandidates.propTypes = {
  candidates: PropTypes.shape({
    radiusKm: PropTypes.number,
    candidates: PropTypes.array.isRequired,
    unlocated: PropTypes.array.isRequired,
  }),
  candidatesError: PropTypes.object,
  candidateRadius: PropTypes.number.isRequired,
  jobBackend: PropTypes.shape({
    invitations: PropTypes.shape({
      send: PropTypes.func.isRequired,
      reload: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  jobData: PropTypes.object,
  setCandidateRadius: PropTypes.func.isRequired,
};

export default React.memo(JobCandidates);
